// Metronome Engine with Web Audio lookahead scheduling
// Adapted from triadmusic-tuner-looper

import { AudioEngine } from './audioEngine';

export const METRONOME_SOUNDS = [
  { value: 'woodblock', labelKey: 'metronome.sndWoodblock' },
  { value: 'click', labelKey: 'metronome.sndClick' },
  { value: 'cowbell', labelKey: 'metronome.sndCowbell' },
  { value: 'kick', labelKey: 'metronome.sndKick' },
  { value: 'snare', labelKey: 'metronome.sndSnare' },
  { value: 'hihat', labelKey: 'metronome.sndHihat' },
];

export class MetronomeEngine {
  constructor(config = {}, onBeat = null) {
    this.config = {
      bpm: 120,
      timeSignature: '4/4',
      accentFirstBeat: true,
      sound: 'woodblock', // see METRONOME_SOUNDS
      volume: 0.8,
      ...config,
    };
    this.isRunning = false;
    this.timerId = null;
    this.nextNoteTime = 0.0;
    this.currentBeatInBar = 0;
    this.onBeatCallback = onBeat;
    this._noiseBuffer = null;
    this._noiseCtx = null;
  }

  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  setOnBeat(cb) {
    this.onBeatCallback = cb;
  }

  start() {
    if (this.isRunning) return;
    const ctx = AudioEngine.getAudioContext();
    this.isRunning = true;
    this.currentBeatInBar = 0;
    this.nextNoteTime = ctx.currentTime + 0.05;

    // Scheduler tick every 25ms
    this.timerId = window.setInterval(() => this.scheduler(), 25);
  }

  stop() {
    this.isRunning = false;
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  getIsRunning() {
    return this.isRunning;
  }

  getTotalBeats() {
    const [num] = (this.config.timeSignature || '4/4').split('/').map(Number);
    return num || 4;
  }

  scheduler() {
    const ctx = AudioEngine.getAudioContext();
    const lookahead = 0.1; // 100ms lookahead

    while (this.nextNoteTime < ctx.currentTime + lookahead) {
      this.scheduleNote(this.nextNoteTime);
      this.advanceNote();
    }
  }

  scheduleNote(time) {
    const isFirstBeat = this.currentBeatInBar === 0;
    const totalBeats = this.getTotalBeats();

    // Trigger visual callback aligned with audio clock
    const ctx = AudioEngine.getAudioContext();
    const delayMs = Math.max(0, (time - ctx.currentTime) * 1000);
    const beatIndex = this.currentBeatInBar;

    setTimeout(() => {
      if (this.isRunning && this.onBeatCallback) {
        this.onBeatCallback(beatIndex, totalBeats, isFirstBeat && this.config.accentFirstBeat);
      }
    }, delayMs);

    // Play click sound
    this.playClick(time, isFirstBeat && this.config.accentFirstBeat);
  }

  advanceNote() {
    const secondsPerBeat = 60.0 / Math.max(30, Math.min(300, this.config.bpm));
    this.nextNoteTime += secondsPerBeat;
    const totalBeats = this.getTotalBeats();
    this.currentBeatInBar = (this.currentBeatInBar + 1) % totalBeats;
  }

  // Buffer de ruído por contexto (reuso entre cliques)
  getNoiseBuffer(ctx) {
    if (!this._noiseBuffer || this._noiseCtx !== ctx) {
      const len = Math.floor(ctx.sampleRate * 1);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
      this._noiseBuffer = buf;
      this._noiseCtx = ctx;
    }
    return this._noiseBuffer;
  }

  playNoise(ctx, time, { dur = 0.15, type = 'highpass', freq = 7000, q = 1, vol = 0.7 }) {
    const src = ctx.createBufferSource();
    src.buffer = this.getNoiseBuffer(ctx);
    src.loop = true;
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    filter.Q.value = q;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(vol, time + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + dur);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);
    src.start(time);
    src.stop(time + dur + 0.05);
  }

  playToneDrop(ctx, time, { from = 160, to = 45, dur = 0.25, type = 'sine', vol = 0.8 }) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(from, time);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), time + dur);
    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(vol, time + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + dur);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(time);
    osc.stop(time + dur + 0.05);
  }

  playKick(ctx, time, vol) {
    this.playToneDrop(ctx, time, { from: 160, to: 42, dur: 0.26, vol });
  }

  playSnare(ctx, time, vol) {
    this.playNoise(ctx, time, { dur: 0.16, type: 'bandpass', freq: 1800, q: 0.9, vol });
    this.playToneDrop(ctx, time, { from: 190, to: 120, dur: 0.09, type: 'triangle', vol: vol * 0.6 });
  }

  playHiHat(ctx, time, vol) {
    this.playNoise(ctx, time, { dur: 0.05, type: 'highpass', freq: 8000, vol: vol * 0.8 });
  }

  playClick(time, isAccent) {
    const ctx = AudioEngine.getAudioContext();
    const vol = this.config.volume || 0.8;

    // Sons de percussão sintetizados no mesmo clock (precisão do lookahead)
    if (this.config.sound === 'kick') {
      this.playKick(ctx, time, isAccent ? vol : vol * 0.6);
      return;
    }
    if (this.config.sound === 'snare') {
      this.playSnare(ctx, time, isAccent ? vol : vol * 0.6);
      return;
    }
    if (this.config.sound === 'hihat') {
      this.playHiHat(ctx, time, isAccent ? vol : vol * 0.6);
      return;
    }

    if (this.config.sound === 'woodblock') {
      const freq = isAccent ? 1200 : 800;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, time);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.4, time + 0.04);

      gain.gain.setValueAtTime(isAccent ? vol : vol * 0.65, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.045);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(time);
      osc.stop(time + 0.05);
    } else if (this.config.sound === 'cowbell') {
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();

      const baseFreq = isAccent ? 800 : 540;
      osc1.type = 'square';
      osc1.frequency.setValueAtTime(baseFreq, time);
      osc2.type = 'square';
      osc2.frequency.setValueAtTime(baseFreq * 1.5, time);

      gain.gain.setValueAtTime(isAccent ? vol * 0.7 : vol * 0.45, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.09);

      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(ctx.destination);

      osc1.start(time);
      osc2.start(time);
      osc1.stop(time + 0.1);
      osc2.stop(time + 0.1);
    } else {
      // standard click
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(isAccent ? 1500 : 900, time);

      gain.gain.setValueAtTime(isAccent ? vol : vol * 0.6, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.025);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(time);
      osc.stop(time + 0.03);
    }
  }
}
